import Minze, { MinzeElement, Reactive, EventListeners } from 'minze'
import { reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth'
import { Routing } from '../../modules/routing'
import { Locale } from '../../modules/locale'
import MainModule from '../../modules/main'

Locale.getInstance().registerKeySet('fr', 'lvcAccountButtonDelete', {
  deleteAccount: 'Supprimer mon compte',
  deleteAccountConfirmation:
    'Êtes-vous sûr de vouloir supprimer votre compte? Cette action est irréversible.',
  deleteAccountConfirmationModalTitle: 'Supprimer mon compte',
  deleteAccountConfirmationModalText:
    'Êtes-vous certains de vouloir supprimer votre compte? Cette action est irréversible.',
  deleteAccountConfirmationModalInputLabel: 'Confirmer mon mot de passe',
  deleteAccountConfirmationModalInputPlaceholder: 'Entrez votre mot de passe',
  deleteAccountConfirmationModalPasswordError: 'Le mot de passe saisi est incorrect',
  deleteAccountConfirmationModalCancelButton: 'Annuler',
  deleteAccountConfirmationModalConfirmButton: 'Supprimer mon compte',
})

Locale.getInstance().registerKeySet('en', 'lvcAccountButtonDelete', {
  deleteAccount: 'Delete my account',
  deleteAccountConfirmation:
    'Are you sure you want to delete your account? This action is irreversible.',
  deleteAccountConfirmationModalTitle: 'Delete my account',
  deleteAccountConfirmationModalText:
    'Are you sure you want to delete your account? This is an  irreversible action.',
  deleteAccountConfirmationModalInputLabel: 'Confirm your password',
  deleteAccountConfirmationModalInputPlaceholder: 'Enter your password',
  deleteAccountConfirmationModalPasswordError: 'The password you entered is incorrect',
  deleteAccountConfirmationModalCancelButton: 'Cancel',
  deleteAccountConfirmationModalConfirmButton: 'Delete my account',
})

export interface LvcAccountButtonDelete {}

export class LvcAccountButtonDelete extends MinzeElement {
  reactive: Reactive = [
    ['email', ''],
    ['providers', []],
    ['deleteConfirmationPasswordError', false],
  ]

  updateInfo() {
    const mainModule = MainModule.getInstance()
    const userAuth = mainModule.getAuth().getUserAuth()

    if (userAuth && userAuth.email) {
      this.email = userAuth.email
      let providers: any[] = []
      if (userAuth.providerData) providers = [...userAuth.providerData]
      this.providers = providers
    } else {
      this.email = ''
    }
    this.rerender()
  }

  onReady(): void | Promise<void> {
    this.updateInfo()
  }

  handleAuthChanged = (event: Event) => {
    this.updateInfo()
  }

  openDeleteAccountModal = (privacyPolicy?: boolean, termsAndConditions?: boolean) => {
    const passwordProvider = (this.providers as Array<any>)?.find(
      provider => provider.providerId === 'password'
    )
    let attr: { name: string; value: string }[] = []
    if (privacyPolicy) {
      attr.push({
        name: 'delete-confirmation-privacy-policy-mode',
        value: 'true',
      })
    }
    if (termsAndConditions) {
      attr.push({
        name: 'delete-confirmation-terms-and-conditions-mode',
        value: 'true',
      })
    }
    if (passwordProvider) {
      attr.push({ name: 'password-provider', value: 'true' })
    }
    MainModule.getInstance().injectIntoDOM('lvc-delete-profile-popup', attr)
  }

  deleteAccount = async (event: Event) => {
    const mainModule = MainModule.getInstance()
    const auth = mainModule.getAuth()
    const detail = (event as CustomEvent).detail
    try {
      this.deleteConfirmationPasswordError = false
      const email = this.email as string
      const passwordProvider = (this.providers as Array<any>)?.find(
        provider => provider.providerId === 'password'
      )
      if (passwordProvider) {
        const password = detail.password as string
        await reauthenticateWithCredential(
          auth.getUserAuth(),
          EmailAuthProvider.credential(email, password)
        )
      }
      let response: any = await auth.deleteAccount(detail.reason || 'none')
      if (response?.success) mainModule.getRouting().goTo(`${Routing.HOST}?account-deleted=true`)
    } catch (e) {
      this.deleteConfirmationPasswordError = Locale.getKey(
        'lvcAccountButtonDelete.deleteAccountConfirmationModalPasswordError'
      )
      Minze.cast('lvc:delete_profile_error', { error: this.deleteConfirmationPasswordError })
    }
  }

  html = () => `
    <button class="account-button-delete" name="deleteAccount">
      <lvc-box-x-icon></lvc-box-x-icon> ${Locale.getKey('lvcAccountButtonDelete.deleteAccount')}
    </button>
  `

  eventListeners: EventListeners = [
    [window, 'lvc:user_state_changed', this.handleAuthChanged.bind(this)],
    [window, 'lvc:delete_profile_confirmed', this.deleteAccount.bind(this)],
    ['[name="deleteAccount"]', 'click', () => this.openDeleteAccountModal()],
  ]

  css = () => `
    :host {}

    .account-button-delete {
      display: flex;
      align-items: center;
      gap: 5px;
      padding: 0px;
      background: transparent;
      border: none;
      color: #1E007D;
      font-family: inherit;
      font-size: 0.875em;
      font-weight: 500;
      text-decoration: underline;
      text-underline-offset: 4px;
      cursor: pointer;
    }

    .account-button-delete:hover {
      text-decoration: none;
    }
  `
}
